import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEOHead from '../components/SEOHead';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { getCategoryBySlug, categoryPages } from '../data/categoryPageData';
import { 
  ArrowRight, CheckCircle2, Type, Code2, Search, 
  Sparkles, Repeat, Users, Briefcase, GraduationCap, Palette, Calculator
} from 'lucide-react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  type: Type,
  code: Code2,
  search: Search,
  sparkles: Sparkles,
  repeat: Repeat,
  calculator: Calculator,
};

const useCaseIcons = [Users, Briefcase, GraduationCap, Palette];

const CategoryPage = () => {
  const { slug } = useParams();
  const [tools, setTools] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = getCategoryBySlug(slug);

  useEffect(() => {
    const fetchData = async () => {
      if (!category) {
        setLoading(false);
        return;
      }

      try {
        // Fetch tools
        const toolsResponse = await axios.get(`${API}/tools`);
        const categoryTools = toolsResponse.data.filter(t => 
          category.tools.includes(t.slug)
        );
        setTools(categoryTools);

        // Fetch blog posts
        const blogResponse = await axios.get(`${API}/blog`);
        const relatedPosts = blogResponse.data.filter(p => 
          category.relatedBlogPosts.includes(p.slug)
        );
        setBlogPosts(relatedPosts);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, category]);

  // Generate ItemList structured data
  const generateItemListSchema = () => ({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": category?.name,
    "description": category?.seoDescription,
    "numberOfItems": tools.length,
    "itemListElement": tools.map((tool, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "SoftwareApplication",
        "name": tool.name,
        "description": tool.short_description,
        "url": `${window.location.origin}/tools/${tool.slug}`,
        "applicationCategory": "WebApplication"
      }
    }))
  });

  // Generate FAQPage structured data
  const generateFAQSchema = () => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": category?.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  });

  // Generate BreadcrumbList structured data
  const generateBreadcrumbSchema = () => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "All Tools",
        "item": `${window.location.origin}/all-tools`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": category?.name,
        "item": `${window.location.origin}/category/${slug}`
      }
    ]
  });

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl font-bold mb-4">Category Not Found</h1>
          <p className="text-muted-foreground mb-6">The category you're looking for doesn't exist.</p>
          <Link to="/all-tools">
            <Button>Browse All Tools</Button>
          </Link>
        </div>
      </div>
    );
  }

  const CategoryIcon = iconMap[category.icon] || Code2;

  return (
    <>
      <SEOHead
        title={category.seoTitle}
        description={category.seoDescription}
        keywords={category.keywords}
        canonical={`${window.location.origin}/category/${slug}`}
      />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(generateItemListSchema()) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(generateFAQSchema()) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(generateBreadcrumbSchema()) }} />

      <div className="min-h-screen pt-24 pb-16 px-6" data-testid={`category-page-${slug}`}>
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <span>/</span>
            <Link to="/all-tools" className="hover:text-foreground transition-colors">All Tools</Link>
            <span>/</span>
            <span className="text-foreground">{category.name}</span>
          </nav>

          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <CategoryIcon className="w-8 h-8 text-primary" />
            </div>
            <Badge variant="outline" className="mb-4 px-4 py-1.5 border-primary/30 text-primary">
              {tools.length} Free Tools
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 font-['Plus_Jakarta_Sans']">
              {category.heroTitle}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {category.heroSubtitle}
            </p>
          </motion.div>

          {/* Introduction */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="prose prose-invert max-w-3xl mx-auto mb-12"
          >
            {category.introduction.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-muted-foreground leading-relaxed">{paragraph}</p>
            ))}
          </motion.div>

          {/* Use Cases */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="grid md:grid-cols-4 gap-4 mb-12"
          >
            {category.useCases.map((useCase, index) => {
              const UseCaseIcon = useCaseIcons[index % useCaseIcons.length];
              return (
                <div key={index} className="p-4 rounded-xl bg-muted/30 border border-border">
                  <UseCaseIcon className="w-6 h-6 text-primary mb-2" />
                  <h3 className="font-semibold mb-1">{useCase.title}</h3>
                  <p className="text-sm text-muted-foreground">{useCase.description}</p>
                </div>
              );
            })}
          </motion.div>

          {/* Tools Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold mb-6 font-['Plus_Jakarta_Sans']">
              {category.name} Available
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tools.map((tool, index) => (
                <motion.div
                  key={tool.slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Link
                    to={`/tools/${tool.slug}`}
                    className="group block p-5 rounded-xl border border-border bg-card hover:border-primary/50 hover:bg-card/80 transition-all h-full"
                    data-testid={`category-tool-${tool.slug}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-semibold group-hover:text-primary transition-colors">
                        {tool.name}
                      </h3>
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${
                          tool.tier === 'free' 
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' 
                            : 'bg-primary/10 text-primary border-primary/30'
                        }`}
                      >
                        {tool.tier}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {tool.short_description}
                    </p>
                    <div className="flex items-center text-primary text-sm">
                      Use Tool <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Features */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="grid md:grid-cols-3 gap-6 mb-16"
          >
            <div className="p-6 rounded-xl bg-muted/30 border border-border">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 mb-3" />
              <h3 className="font-semibold mb-2">100% Free</h3>
              <p className="text-sm text-muted-foreground">
                All {category.name.toLowerCase()} are completely free with no signup or credit card required.
              </p>
            </div>
            <div className="p-6 rounded-xl bg-muted/30 border border-border">
              <CheckCircle2 className="w-8 h-8 text-primary mb-3" />
              <h3 className="font-semibold mb-2">Privacy First</h3>
              <p className="text-sm text-muted-foreground">
                All processing happens in your browser. Your data never leaves your device.
              </p>
            </div>
            <div className="p-6 rounded-xl bg-muted/30 border border-border">
              <CheckCircle2 className="w-8 h-8 text-accent mb-3" />
              <h3 className="font-semibold mb-2">No Installation</h3>
              <p className="text-sm text-muted-foreground">
                Works instantly in any modern browser. No downloads or plugins needed.
              </p>
            </div>
          </motion.div>

          {/* FAQ Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold mb-6 font-['Plus_Jakarta_Sans']">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="w-full max-w-3xl">
              {category.faqs.map((faq, index) => (
                <AccordionItem key={index} value={`faq-${index}`} className="border-border">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-medium">{faq.q}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>

          {/* Related Blog Posts */}
          {blogPosts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              className="mb-16"
            >
              <h2 className="text-2xl font-bold mb-6 font-['Plus_Jakarta_Sans']">
                Related Articles
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                {blogPosts.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="group p-4 rounded-xl border border-border bg-card hover:border-primary/30 transition-colors"
                  >
                    <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {post.excerpt}
                    </p>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}

          {/* Related Categories */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold mb-6 font-['Plus_Jakarta_Sans']">
              Explore Other Categories
            </h2>
            <div className="flex flex-wrap gap-3">
              {category.relatedCategories.map((relatedSlug) => {
                const relatedCat = categoryPages[relatedSlug];
                if (!relatedCat) return null;
                const RelatedIcon = iconMap[relatedCat.icon] || Code2;
                return (
                  <Link
                    key={relatedSlug}
                    to={`/category/${relatedSlug}`}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-card hover:border-primary/50 hover:bg-card/80 transition-all"
                  >
                    <RelatedIcon className="w-4 h-4 text-primary" />
                    <span>{relatedCat.name}</span>
                  </Link>
                );
              })}
              <Link
                to="/all-tools"
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-primary/30 bg-primary/10 hover:bg-primary/20 transition-colors"
              >
                View All Tools
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.45 }}
            className="text-center p-8 rounded-2xl bg-gradient-to-r from-primary/10 to-accent/10 border border-border"
          >
            <h2 className="text-2xl font-bold mb-3 font-['Plus_Jakarta_Sans']">
              Ready to Get Started?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              Start using our {category.name.toLowerCase()} right now. No signup required, completely free.
            </p>
            <div className="flex justify-center gap-4">
              <Link to={`/tools/${tools[0]?.slug || ''}`}>
                <Button className="glow-blue">
                  Try {tools[0]?.name || 'First Tool'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default CategoryPage;
